import React, { useState, useEffect } from 'react'
import { useDataProvider, useNotify } from 'react-admin'
import {
  Box,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Typography,
  Button as MuiButton,
  Link,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { VendorQuestionnaireStatuses } from './types'

// Define TypeScript interfaces for questionnaires
interface QuestionnaireTemplate {
  questionnaire?: {
    title: string
    fields?: {
      source: string
      label: string
      fieldType: string
      options?: {
        text: string
        score?: number
      }[]
      // For yes/no fields
      yesScore?: number
      noScore?: number
      allow_vendor_comment?: boolean
      required?: boolean
    }[]
  }[]
  min_risk_score?: number
  max_risk_score?: number
  allow_additional_document_uploads?: boolean
}

interface Attachment {
  id: number
  vendor_questionnaire_id: number
  field_id: string
  field_source?: string
  original_filename: string
  file_type: string
  file_size: number
  uploaded_at: string
}

interface QuestionnaireDetails {
  template?: QuestionnaireTemplate
  completed?: Record<string, string>
}

interface VendorQuestionnaire {
  id: number
  questionnaire_id: string
  status: number
  vendor_email_address: string
  agent_user_id: number
  completed_questionnaire?: Record<string, string>
  score?: number
  percentage_score?: number
  vendor_id: number
  completed_at?: string
  clarification_requests?: Record<string, { question: string; comment: string }>
  clarification_responses?: Record<string, string>
  clarification_requested_at?: string
  clarification_responded_at?: string
  clarification_history?: Array<{
    round: number
    requested_at: string
    responded_at?: string
    requests: Record<string, { question: string; comment: string }>
    responses?: Record<string, string>
  }>
  vendor?: {
    name: string
    [key: string]: any
  }
  questionnaire?: {
    id: string
    name: string
    description: string
    type?: string
    [key: string]: any
  }
}

interface QuestionnaireReviewFormProps {
  open: boolean
  onClose: () => void
  selectedQuestionnaire: VendorQuestionnaire | null
  viewSource?: string | null
  onAccept?: () => void
  onReject?: () => void
  onRefresh?: () => void
}

const QuestionnaireReviewForm: React.FC<QuestionnaireReviewFormProps> = ({
  open,
  onClose,
  selectedQuestionnaire,
  viewSource = 'default',
  onAccept,
  onReject,
  onRefresh,
}) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()

  const [questionnaireDetails, setQuestionnaireDetails] =
    useState<QuestionnaireDetails | null>(null)
  const [overrideScore, setOverrideScore] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [clarificationRequests, setClarificationRequests] = useState<
    Record<string, { question: string; comment: string }>
  >({})
  const [clarificationResponses, setClarificationResponses] = useState<
    Record<string, string>
  >({})
  const [clarificationHistory, setClarificationHistory] = useState<
    Array<{
      round: number
      requested_at: string
      responded_at?: string
      requests: Record<string, { question: string; comment: string }>
      responses?: Record<string, string>
    }>
  >([])
  const [showClarificationMode, setShowClarificationMode] = useState(false)
  const [hasClarificationHistory, setHasClarificationHistory] = useState(false)

  // Helper function to construct attachment download URL
  const getAttachmentDownloadUrl = (attachment: Attachment) => {
    // Add a timestamp to prevent caching
    const timestamp = new Date().getTime()
    // Use the public endpoint to ensure it works even if the user is not authenticated
    // Use absolute URL to bypass proxy and hit backend server directly
    return `http://localhost:8888/api/v1/questionnaires/public/vendors/${selectedQuestionnaire?.vendor_id}/${attachment.vendor_questionnaire_id}/attachments/${attachment.id}?download=true&t=${timestamp}`
  }

  // Load questionnaire details when a questionnaire is selected
  useEffect(() => {
    const loadQuestionnaireDetails = async () => {
      if (!selectedQuestionnaire) return

      setLoading(true)
      try {
        // Check if there are clarification requests and responses
        if (selectedQuestionnaire.clarification_requests) {
          setClarificationRequests(selectedQuestionnaire.clarification_requests)
        } else {
          setClarificationRequests({})
        }

        if (selectedQuestionnaire.clarification_responses) {
          setClarificationResponses(
            selectedQuestionnaire.clarification_responses
          )
          // If we have both requests and responses, set the history flag
          if (selectedQuestionnaire.clarification_requests) {
            setHasClarificationHistory(true)
          }
        } else {
          setClarificationResponses({})
          setHasClarificationHistory(false)
        }

        // Load clarification history if available
        if (
          selectedQuestionnaire.clarification_history &&
          selectedQuestionnaire.clarification_history.length > 0
        ) {
          setClarificationHistory(selectedQuestionnaire.clarification_history)
          setHasClarificationHistory(true)
        } else {
          setClarificationHistory([])
        }

        // Use the existing data from the selectedQuestionnaire
        if (
          selectedQuestionnaire.completed_questionnaire &&
          selectedQuestionnaire.questionnaire
        ) {
          // Fetch only the questionnaire template structure
          const { data: questionnaireTemplate } = await dataProvider.custom(
            `questionnaires/${selectedQuestionnaire.questionnaire_id}/organisations/current`,
            { method: 'GET' }
          )

          // Set the questionnaire details with both the template and the completed data
          setQuestionnaireDetails({
            template: questionnaireTemplate,
            completed: selectedQuestionnaire.completed_questionnaire,
          })
        } else {
          // Fallback to the old method if the data is not available
          const { data } = await dataProvider.custom(
            `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}/details`,
            { method: 'GET' }
          )
          setQuestionnaireDetails(data)
        }

        // Fetch attachments for the questionnaire
        try {
          console.log('Selected questionnaire:', selectedQuestionnaire)
          console.log('Selected questionnaire ID:', selectedQuestionnaire.id)

          const { data: attachmentsData } = await dataProvider.custom(
            `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}/attachments`,
            { method: 'GET' }
          )
          console.log('Attachments data:', attachmentsData)

          // Log the vendor_questionnaire_id from the first attachment if available
          if (attachmentsData && attachmentsData.length > 0) {
            console.log(
              'First attachment vendor_questionnaire_id:',
              attachmentsData[0].vendor_questionnaire_id
            )
          }

          setAttachments(attachmentsData || [])
        } catch (attachmentError) {
          console.error(
            'Error loading questionnaire attachments:',
            attachmentError
          )
          // Don't show an error notification for attachments, just log it
          // This way, the questionnaire can still be viewed even if attachments fail to load
          setAttachments([])
        }
      } catch (error) {
        console.error('Error loading questionnaire details:', error)
        notify('Error loading questionnaire details', { type: 'error' })
      } finally {
        setLoading(false)
      }
    }

    if (open && selectedQuestionnaire) {
      loadQuestionnaireDetails()
    } else {
      setQuestionnaireDetails(null)
      setOverrideScore(null)
      setAttachments([])
      setClarificationRequests({})
      setClarificationResponses({})
      setClarificationHistory([])
      setHasClarificationHistory(false)
      setShowClarificationMode(false)
    }
  }, [open, selectedQuestionnaire, dataProvider, notify])

  // Handle accepting a questionnaire (change status to Assessed)
  const handleAcceptQuestionnaire = async () => {
    if (!selectedQuestionnaire) return

    try {
      // Update the questionnaire status using the API
      const updatedData: any = {
        status: VendorQuestionnaireStatuses.Completed, // Set status to "Completed"
      }

      // If score was overridden, include it in the update
      if (overrideScore !== null) {
        updatedData.score = overrideScore

        // Calculate percentage score if min and max risk scores are available
        if (
          questionnaireDetails?.template?.min_risk_score !== undefined &&
          questionnaireDetails?.template?.max_risk_score !== undefined
        ) {
          const minScore = questionnaireDetails.template.min_risk_score
          const maxScore = questionnaireDetails.template.max_risk_score
          const range = maxScore - minScore

          const percentageScore =
            range > 0 ? ((overrideScore - minScore) / range) * 100 : 0

          updatedData.percentage_score = Math.max(
            0,
            Math.min(100, percentageScore)
          )
        }
      }

      await dataProvider.custom(
        `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        }
      )

      // Close the dialog
      onClose()

      // Refresh the questionnaires list
      if (onRefresh) {
        onRefresh()
      }

      // Call the onAccept callback if provided
      if (onAccept) {
        onAccept()
      }

      notify('Questionnaire accepted successfully', { type: 'success' })
    } catch (error) {
      console.error('Error accepting questionnaire:', error)
      notify('Error accepting questionnaire', { type: 'error' })
    }
  }

  // Handle rejecting a questionnaire (change status to Rejected)
  const handleRejectQuestionnaire = async () => {
    if (!selectedQuestionnaire) return

    try {
      // Update the questionnaire status using the API
      const updatedData = {
        status: VendorQuestionnaireStatuses.Rejected, // Set status to "Rejected"
      }

      await dataProvider.custom(
        `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        }
      )

      // Close the dialog
      onClose()

      // Refresh the questionnaires list
      if (onRefresh) {
        onRefresh()
      }

      // Call the onReject callback if provided
      if (onReject) {
        onReject()
      }

      notify('Questionnaire rejected successfully', { type: 'success' })
    } catch (error) {
      console.error('Error rejecting questionnaire:', error)
      notify('Error rejecting questionnaire', { type: 'error' })
    }
  }

  // Toggle clarification mode
  const toggleClarificationMode = () => {
    const newMode = !showClarificationMode
    setShowClarificationMode(newMode)
    // Clear existing clarification requests when toggling clarification mode
    // This ensures we don't autofill previous clarification requests when entering clarification mode
    setClarificationRequests({})
  }

  // Handle adding or updating a clarification request for a specific question
  const handleClarificationRequest = (
    fieldSource: string,
    question: string,
    comment: string
  ) => {
    setClarificationRequests((prev) => ({
      ...prev,
      [fieldSource]: { question, comment },
    }))
  }

  // Handle removing a clarification request
  const handleRemoveClarificationRequest = (fieldSource: string) => {
    setClarificationRequests((prev) => {
      const newRequests = { ...prev }
      delete newRequests[fieldSource]
      return newRequests
    })
  }

  // Handle sending all clarification requests
  const handleSendClarificationRequests = async () => {
    if (
      !selectedQuestionnaire ||
      Object.keys(clarificationRequests).length === 0
    ) {
      notify('No clarification requests to send', { type: 'warning' })
      return
    }

    try {
      // Update the questionnaire status and include clarification requests
      const updatedData = {
        status: VendorQuestionnaireStatuses.ClarificationRequested, // Set status to "ClarificationRequested"
        clarification_requests: clarificationRequests,
      }

      await dataProvider.custom(
        `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        }
      )

      // Close the dialog
      onClose()

      // Refresh the questionnaires list
      if (onRefresh) {
        onRefresh()
      }

      notify('Clarification requests sent successfully', { type: 'success' })
    } catch (error) {
      console.error('Error sending clarification requests:', error)
      notify('Error sending clarification requests', { type: 'error' })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '90%',
          maxWidth: '1200px',
          height: '90vh',
          maxHeight: '900px',
        },
      }}
    >
      <DialogTitle>
        Completed Questionnaire
        {selectedQuestionnaire?.score !== undefined &&
          selectedQuestionnaire?.questionnaire?.type !== 'preliminary' && (
            <Box component="span" ml={2}>
              Score: {selectedQuestionnaire.score}
              {selectedQuestionnaire.percentage_score !== undefined &&
                ` (${selectedQuestionnaire.percentage_score}%)`}
            </Box>
          )}
      </DialogTitle>
      {hasClarificationHistory && (
        <Box mx={3} mt={1} p={2} bgcolor="#e3f2fd" borderRadius={1}>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            This questionnaire has clarification history
          </Typography>

          {clarificationHistory.length > 0 ? (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>{clarificationHistory.length}</strong> round(s) of
                clarification have been completed.
              </Typography>

              <Box display="flex" flexDirection="column" gap={1} mt={1}>
                {clarificationHistory.map((round, index) => (
                  <Box key={index} display="flex" flexDirection="row" gap={4}>
                    <Typography variant="body2">
                      <strong>Round {round.round}:</strong> Requested{' '}
                      {new Date(round.requested_at).toLocaleString()}
                      {round.responded_at &&
                        `, Responded ${new Date(
                          round.responded_at
                        ).toLocaleString()}`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box display="flex" flexDirection="row" gap={4}>
              {selectedQuestionnaire?.clarification_requested_at && (
                <Typography variant="body2">
                  <strong>Clarification Requested:</strong>{' '}
                  {new Date(
                    selectedQuestionnaire.clarification_requested_at
                  ).toLocaleString()}
                </Typography>
              )}
              {selectedQuestionnaire?.clarification_responded_at && (
                <Typography variant="body2">
                  <strong>Clarification Responded:</strong>{' '}
                  {new Date(
                    selectedQuestionnaire.clarification_responded_at
                  ).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="body2" mt={1}>
            <strong>{Object.keys(clarificationResponses).length}</strong>{' '}
            question(s) have clarification responses. Look for the
            &quot;Clarification History&quot; sections below.
          </Typography>
        </Box>
      )}
      <DialogContent>
        {questionnaireDetails ? (
          <Box>
            {questionnaireDetails.template?.questionnaire?.map(
              (section, sectionIndex) => (
                <Paper key={sectionIndex} elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {section.title}
                  </Typography>
                  <Box>
                    {section.fields?.map((field, fieldIndex) => {
                      const answer =
                        questionnaireDetails.completed?.[field.source] || ''
                      return (
                        <Box key={fieldIndex} mb={2}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              {field.label}
                            </Typography>
                            {showClarificationMode && (
                              <Tooltip title="Request clarification for this question">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        !!clarificationRequests[field.source]
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          handleClarificationRequest(
                                            field.source,
                                            field.label,
                                            ''
                                          )
                                        } else {
                                          handleRemoveClarificationRequest(
                                            field.source
                                          )
                                        }
                                      }}
                                      color="primary"
                                    />
                                  }
                                  label="Request Clarification"
                                />
                              </Tooltip>
                            )}
                          </Box>
                          {answer === 'attachment_uploaded' ||
                          field.fieldType === 'attachment' ? (
                            <>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                              >
                                Field Type: Attachment
                              </Typography>
                              {/* Display attachments for this field if any */}
                              {attachments
                                .filter((attachment) => {
                                  // Check if field_source matches field.source exactly
                                  if (
                                    attachment.field_source &&
                                    attachment.field_source === field.source
                                  ) {
                                    return true
                                  }

                                  // Fallback to exact matching with field_id only if field_source is not available
                                  return (
                                    !attachment.field_source &&
                                    attachment.field_id === field.source
                                  )
                                })
                                .map((attachment) => (
                                  <Box
                                    key={attachment.id}
                                    mt={1}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <AttachFileIcon
                                      fontSize="small"
                                      sx={{ mr: 1 }}
                                    />
                                    <Link
                                      href={getAttachmentDownloadUrl(
                                        attachment
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="hover"
                                    >
                                      {attachment.original_filename}
                                    </Link>
                                  </Box>
                                ))}
                              {attachments.filter((attachment) => {
                                // Check if field_source matches field.source exactly
                                if (
                                  attachment.field_source &&
                                  attachment.field_source === field.source
                                ) {
                                  return true
                                }

                                // Fallback to exact matching with field_id only if field_source is not available
                                return (
                                  !attachment.field_source &&
                                  attachment.field_id === field.source
                                )
                              }).length === 0 && (
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  No attachments found
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography variant="body1" color="textSecondary">
                              Answer: {answer}
                            </Typography>
                          )}
                          {/* Display attachments for non-attachment fields if any */}
                          {/* Show clarification comment input when in clarification mode */}
                          {showClarificationMode &&
                            clarificationRequests[field.source] && (
                              <Box mt={1} mb={2}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  label="Clarification Comment"
                                  variant="outlined"
                                  value={
                                    clarificationRequests[field.source]
                                      ?.comment || ''
                                  }
                                  onChange={(e) =>
                                    handleClarificationRequest(
                                      field.source,
                                      field.label,
                                      e.target.value
                                    )
                                  }
                                  placeholder="Explain what clarification you need from the supplier..."
                                />
                              </Box>
                            )}

                          {/* Show clarification history if this field has clarification history */}
                          {!showClarificationMode &&
                            hasClarificationHistory && (
                              <>
                                {/* Show history from clarification_history if available */}
                                {clarificationHistory.length > 0 ? (
                                  <Box
                                    mt={2}
                                    p={2}
                                    bgcolor="#f8f9fa"
                                    borderRadius={1}
                                    border="1px solid #e9ecef"
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      color="primary"
                                      gutterBottom
                                    >
                                      Clarification History
                                    </Typography>

                                    <Box mb={1}>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                      >
                                        Original Answer:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {answer}
                                      </Typography>
                                    </Box>

                                    {/* Display each round of clarification */}
                                    {clarificationHistory.map(
                                      (round, index) => {
                                        // Only show if this field has clarification in this round
                                        if (
                                          round.requests &&
                                          round.requests[field.source]
                                        ) {
                                          return (
                                            <Box
                                              key={index}
                                              mt={2}
                                              p={2}
                                              bgcolor={
                                                index % 2 === 0
                                                  ? '#f1f8e9'
                                                  : '#e8f5e9'
                                              }
                                              borderRadius={1}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                              >
                                                Round {round.round} -{' '}
                                                {new Date(
                                                  round.requested_at
                                                ).toLocaleDateString()}
                                              </Typography>

                                              <Box mb={1}>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="bold"
                                                >
                                                  Clarification Request:
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  color="textSecondary"
                                                >
                                                  {
                                                    round.requests[field.source]
                                                      ?.comment
                                                  }
                                                </Typography>
                                              </Box>

                                              {round.responses &&
                                                round.responses[
                                                  field.source
                                                ] && (
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="bold"
                                                    >
                                                      Supplier Response:
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      color="textSecondary"
                                                    >
                                                      {
                                                        round.responses[
                                                          field.source
                                                        ]
                                                      }
                                                    </Typography>
                                                  </Box>
                                                )}
                                              {(!round.responses ||
                                                !round.responses[
                                                  field.source
                                                ]) &&
                                                round.responded_at && (
                                                  <Box>
                                                    <Typography
                                                      variant="body2"
                                                      fontStyle="italic"
                                                      color="warning.main"
                                                    >
                                                      Response was submitted on{' '}
                                                      {new Date(
                                                        round.responded_at
                                                      ).toLocaleDateString()}{' '}
                                                      but no specific answer for
                                                      this question was
                                                      provided.
                                                    </Typography>
                                                  </Box>
                                                )}
                                            </Box>
                                          )
                                        }
                                        return null
                                      }
                                    )}
                                  </Box>
                                ) : (
                                  // Fallback to the old way if clarification_history is not available
                                  clarificationRequests[field.source] && (
                                    <Box
                                      mt={2}
                                      p={2}
                                      bgcolor="#f8f9fa"
                                      borderRadius={1}
                                      border="1px solid #e9ecef"
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        gutterBottom
                                      >
                                        Clarification History
                                      </Typography>
                                      <Box mb={1}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                        >
                                          Original Answer:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          {answer}
                                        </Typography>
                                      </Box>
                                      <Box mb={1}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                        >
                                          Clarification Request:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          {
                                            clarificationRequests[field.source]
                                              ?.comment
                                          }
                                        </Typography>
                                      </Box>
                                      {clarificationResponses[field.source] ? (
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                          >
                                            Supplier Response:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            {
                                              clarificationResponses[
                                                field.source
                                              ]
                                            }
                                          </Typography>
                                        </Box>
                                      ) : (
                                        selectedQuestionnaire?.clarification_responded_at && (
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              fontStyle="italic"
                                              color="warning.main"
                                            >
                                              Response was submitted on{' '}
                                              {new Date(
                                                selectedQuestionnaire.clarification_responded_at
                                              ).toLocaleDateString()}{' '}
                                              but no specific answer for this
                                              question was provided.
                                            </Typography>
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                  )
                                )}
                              </>
                            )}
                          {answer !== 'attachment_uploaded' && (
                            <>
                              {attachments
                                .filter((attachment) => {
                                  // Check if field_source matches field.source exactly
                                  if (
                                    attachment.field_source &&
                                    attachment.field_source === field.source
                                  ) {
                                    return true
                                  }

                                  // Fallback to exact matching with field_id only if field_source is not available
                                  return (
                                    !attachment.field_source &&
                                    attachment.field_id === field.source
                                  )
                                })
                                .map((attachment) => (
                                  <Box
                                    key={attachment.id}
                                    mt={1}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <AttachFileIcon
                                      fontSize="small"
                                      sx={{ mr: 1 }}
                                    />
                                    <Link
                                      href={getAttachmentDownloadUrl(
                                        attachment
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="hover"
                                    >
                                      {attachment.original_filename}
                                    </Link>
                                  </Box>
                                ))}
                            </>
                          )}
                          {field.fieldType === 'dropdown' &&
                            field.options &&
                            selectedQuestionnaire?.questionnaire?.type !==
                              'preliminary' && (
                              <>
                                {/* Show score for the selected option */}
                                {field.options.map((option, optionIndex) => {
                                  if (option.text === answer) {
                                    return (
                                      <Typography
                                        key={optionIndex}
                                        variant="body2"
                                        color="primary"
                                      >
                                        Score:{' '}
                                        {option.score !== undefined
                                          ? option.score
                                          : 0}
                                      </Typography>
                                    )
                                  }
                                  return null
                                })}

                                {/* Show all possible scores for this field */}
                                <Box mt={1}>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    All possible scores:
                                  </Typography>
                                  {field.options.map((option, optionIndex) => (
                                    <Typography
                                      key={`score-${optionIndex}`}
                                      variant="body2"
                                      color={
                                        option.text === answer
                                          ? 'primary'
                                          : 'textSecondary'
                                      }
                                      sx={{ ml: 2 }}
                                    >
                                      {option.text}:{' '}
                                      {option.score !== undefined
                                        ? option.score
                                        : 0}
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          {field.fieldType === 'text' && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Field Type: Text Box
                              </Typography>
                              {selectedQuestionnaire?.questionnaire?.type !==
                                'preliminary' && (
                                <Typography variant="body2" color="primary">
                                  Score: 0
                                </Typography>
                              )}
                            </>
                          )}

                          {/* Display "Required/Optional" and "Vendor Comment Allowed" if allow_vendor_comment is true */}
                          {field.allow_vendor_comment && (
                            <>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                mt={1}
                              >
                                {field.required ? 'Required' : 'Optional'}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                mt={1}
                              >
                                Vendor Comment Allowed
                              </Typography>

                              {/* Display vendor comment if it exists */}
                              {questionnaireDetails.completed?.[
                                `${field.source}_vendor_comment`
                              ] && (
                                <Box
                                  mt={2}
                                  p={2}
                                  bgcolor="#f5f5f5"
                                  borderRadius={1}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                    gutterBottom
                                  >
                                    Vendor Comment:
                                  </Typography>
                                  <Typography variant="body2">
                                    {
                                      questionnaireDetails.completed[
                                        `${field.source}_vendor_comment`
                                      ]
                                    }
                                  </Typography>
                                </Box>
                              )}
                            </>
                          )}
                          {field.fieldType === 'yesno' && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Field Type: Yes/No
                              </Typography>
                              {selectedQuestionnaire?.questionnaire?.type !==
                                'preliminary' && (
                                <>
                                  {/* Show score for the selected answer */}
                                  <Typography variant="body2" color="primary">
                                    Score:{' '}
                                    {answer === 'yes'
                                      ? field.yesScore !== undefined
                                        ? field.yesScore
                                        : 0
                                      : answer === 'no'
                                      ? field.noScore !== undefined
                                        ? field.noScore
                                        : 0
                                      : 0}
                                  </Typography>

                                  {/* Show all possible scores for this field */}
                                  <Box mt={1}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      All possible scores:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color={
                                        answer === 'yes'
                                          ? 'primary'
                                          : 'textSecondary'
                                      }
                                      sx={{ ml: 2 }}
                                    >
                                      Yes:{' '}
                                      {field.yesScore !== undefined
                                        ? field.yesScore
                                        : 0}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color={
                                        answer === 'no'
                                          ? 'primary'
                                          : 'textSecondary'
                                      }
                                      sx={{ ml: 2 }}
                                    >
                                      No:{' '}
                                      {field.noScore !== undefined
                                        ? field.noScore
                                        : 0}
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </Paper>
              )
            )}

            {/* Display "Allow Additional Document Uploads" information */}
            {questionnaireDetails?.template
              ?.allow_additional_document_uploads && (
              <Box mt={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="body1">
                  Allow Additional Document Uploads: Yes
                </Typography>

                {/* Display additional document uploads if any */}
                {attachments
                  .filter(
                    (attachment) =>
                      attachment.field_id === 'additional_document_uploads' ||
                      attachment.field_source === 'additional_document_uploads'
                  )
                  .map((attachment) => (
                    <Box
                      key={attachment.id}
                      mt={1}
                      display="flex"
                      alignItems="center"
                    >
                      <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
                      <Link
                        href={getAttachmentDownloadUrl(attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {attachment.original_filename}
                      </Link>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      {/* Score Override Slider - Only show when reviewing and not a preliminary questionnaire */}
      {viewSource === 'review' &&
        questionnaireDetails?.template &&
        selectedQuestionnaire?.questionnaire?.type !== 'preliminary' && (
          <Box px={3} pb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Override Risk Score
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">
                Min: {questionnaireDetails.template.min_risk_score || 0}
              </Typography>
              <Slider
                value={
                  overrideScore !== null
                    ? overrideScore
                    : selectedQuestionnaire?.score || 0
                }
                onChange={(_, newValue) => setOverrideScore(newValue as number)}
                min={questionnaireDetails.template.min_risk_score || 0}
                max={questionnaireDetails.template.max_risk_score || 100}
                step={0.1}
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <Typography variant="body2">
                Max: {questionnaireDetails.template.max_risk_score || 100}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography variant="body2" color="textSecondary">
                Original Score: {selectedQuestionnaire?.score || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                New Score:{' '}
                {overrideScore !== null
                  ? overrideScore
                  : selectedQuestionnaire?.score || 0}
              </Typography>
            </Box>
          </Box>
        )}
      <DialogActions>
        <MuiButton variant="outlined" onClick={onClose}>
          Close
        </MuiButton>
        {viewSource === 'review' && (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {showClarificationMode ? (
              <>
                <MuiButton
                  variant="outlined"
                  onClick={toggleClarificationMode}
                  color="secondary"
                >
                  Cancel Clarification
                </MuiButton>
                <MuiButton
                  variant="contained"
                  onClick={handleSendClarificationRequests}
                  color="primary"
                  disabled={Object.keys(clarificationRequests).length === 0}
                >
                  Send Clarification Request
                </MuiButton>
              </>
            ) : (
              <>
                <MuiButton
                  variant="outlined"
                  onClick={toggleClarificationMode}
                  color="info"
                >
                  Request Clarification
                </MuiButton>
                <MuiButton
                  variant="outlined"
                  onClick={handleRejectQuestionnaire}
                  color="error"
                >
                  Reject
                </MuiButton>
                <MuiButton
                  variant="contained"
                  onClick={handleAcceptQuestionnaire}
                  color="primary"
                >
                  Accept
                </MuiButton>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default QuestionnaireReviewForm
